import { HttpClient } from '../api/client'
import type { ApiResponse } from '@bandos/shared'
import { getAuthenticatedHeaders, transformResponse } from './aiHelpers'
import type { UsageMetrics } from './aiTypes'

interface StakeholderRating {
  name: string
  role: string
  rating: number
  whatWorks: string
  mainRisks: string
  suggestions: string
  question: string
}

interface RatingsData {
  stakeholders: StakeholderRating[]
}

interface GenerateRatingsResult {
  ratings: RatingsData
  usage?: UsageMetrics
  phase?: number
}

interface AssignPhaseResult {
  phase?: number
  usage?: UsageMetrics
}

/**
 * AI service for idea evaluation — ratings generation and phase assignment.
 */
export class AIRatingsService {
  /**
   * Generate stakeholder ratings for an idea node.
   * @param onlyForStakeholderNames - Optional array of stakeholder names for partial regeneration.
   */
  static async generateRatings(
    mindmapId: string,
    nodeId: string,
    onlyForStakeholderNames?: string[]
  ): Promise<ApiResponse<GenerateRatingsResult>> {
    const auth = await getAuthenticatedHeaders<GenerateRatingsResult>()
    if (auth.error) return auth.error

    const requestData: { mindmapId: string; nodeId: string; onlyForStakeholderNames?: string[] } = {
      mindmapId,
      nodeId,
    }

    if (Array.isArray(onlyForStakeholderNames)) {
      requestData.onlyForStakeholderNames = onlyForStakeholderNames
    }

    const response = await HttpClient.post<{
      success: boolean
      message?: string
      ratings?: RatingsData
      usage?: UsageMetrics
      phase?: number
    }>('/ai/generate-ratings', requestData, auth.headers)

    return transformResponse(
      response,
      (raw) =>
        raw.success && raw.ratings
          ? { ratings: raw.ratings, usage: raw.usage, phase: raw.phase }
          : null,
      'GENERATE_RATINGS_ERROR',
      'Failed to generate ratings'
    )
  }

  /** Assign ideation phase to an idea node based on its content */
  static async assignPhase(
    mindmapId: string,
    nodeId: string
  ): Promise<ApiResponse<AssignPhaseResult>> {
    const auth = await getAuthenticatedHeaders<AssignPhaseResult>()
    if (auth.error) return auth.error

    const response = await HttpClient.post<{
      success: boolean
      message?: string
      phase?: number
      usage?: UsageMetrics
    }>('/ai/assign-phase', { mindmapId, nodeId }, auth.headers)

    return transformResponse(
      response,
      (raw) => (raw.success ? { phase: raw.phase, usage: raw.usage } : null),
      'ASSIGN_PHASE_ERROR',
      'Failed to assign phase'
    )
  }
}
